import { FC, HTMLAttributes, memo, useCallback, useState, MouseEvent } from 'react';
import cn from 'classnames';

import useMedias from '@Hooks/useMedias';
import ButtonTabs, { Tab } from '@UI/ButtonTabs';
import Gallery, { ProgressOptions } from '@UI/Gallery';
import ProgressBar from '@UI/ProgressBar';
import NavSideArrows from '@Components/NavSideArrows';
import BackgroundImage from '@UI/BackgroundImage';
import Section from '@Components/Section';
import styles from './ShowroomsGallery.module.css';

export interface ShowroomsGalleryProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  title: string;
  tabs: Tab[];
  selectedTab?: string;
  images: string[];
  onChangeTab?: (e: MouseEvent, tab: Tab) => void;
}

const ShowroomsGallery: FC<ShowroomsGalleryProps> = (props) => {
  const { className, title, tabs, selectedTab, images, onChangeTab, ...restProps } = props;
  const { isMobileM } = useMedias();
  const [track, setTrack] = useState<ProgressOptions>(null);
  const [slideIndex, setSlideIndex] = useState(0);

  const normalizeSlide = useCallback(
    (index: number) => {
      if (index < 0) return 0;
      if (index > images.length) return images.length;

      return index;
    },
    [images.length],
  );

  const handleChangeProgress = useCallback((opts: ProgressOptions) => {
    setTrack(opts);
  }, []);

  const handleChangeCurrent = useCallback(({ current }) => {
    setSlideIndex(current);
  }, []);

  const handlePrev = useCallback(() => {
    setSlideIndex((prev) => normalizeSlide(prev - 1));
  }, [normalizeSlide]);

  const handleNext = useCallback(() => {
    if (track && track.finished) return;

    setSlideIndex((prev) => normalizeSlide(prev + 1));
  }, [normalizeSlide, track]);

  const handleChangeTab = useCallback(
    (e, tab) => {
      setSlideIndex(0);

      if (onChangeTab) onChangeTab(e, tab);
    },
    [onChangeTab],
  );

  const handleClickPreview = useCallback((e, index) => {
    if (window.cancelClick) return;

    setSlideIndex(index);
  }, []);

  return (
    <div {...restProps} className={cn(styles.slider, className)}>
      <Section className={styles.title} title={title} />

      <div className={styles.tabWrapper}>
        <ButtonTabs scrollable value={selectedTab} tabs={tabs} onChangeTab={handleChangeTab} />
      </div>

      {images.length > 0 ? (
        <>
          <div className={styles.container}>
            <div className={styles.galleryInner}>
              {!isMobileM && <NavSideArrows onPrev={handlePrev} onNext={handleNext} />}

              <Gallery
                className={styles.gallery}
                slideIndex={slideIndex}
                onChangeProgress={handleChangeProgress}
                onChangeCurrent={handleChangeCurrent}
              >
                {images.map((src) => (
                  <div key={src} className={styles.slideWrapper}>
                    <div className={styles.slide}>
                      <BackgroundImage className={styles.imgWrapper} src={src} />
                    </div>
                  </div>
                ))}
              </Gallery>
            </div>

            {!isMobileM && (
              <div className={styles.galleryPaginationWrapper}>
                <Gallery className={styles.galleryPagination} slideIndex={slideIndex}>
                  {images.map((src, index) => (
                    <div
                      key={src}
                      className={cn(styles.slidePagination, {
                        [styles.actived]: slideIndex === index,
                      })}
                      onClick={(e) => handleClickPreview(e, index)}
                    >
                      <BackgroundImage className={styles.imgWrapper} src={src} />
                    </div>
                  ))}
                </Gallery>
              </div>
            )}
          </div>

          {track?.width < 100 && (
            <div className={styles.wrapper}>
              <ProgressBar track={track} />
            </div>
          )}
        </>
      ) : (
        <div className={cn(styles.container, styles.placeholder)}>
          <div className={styles.galleryPlaceholder}>
            <div className={styles.galleryPlaceholderText}>
              Скоро здесь появятся красивые фотографии нового шоу-рума
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ShowroomsGallery);
